/**
 * CrmAsrEditor — author an Additional Services Request: title, issue date, fee type, a line-item fee
 * table (Fixed lump sums + Hourly), and stipulations. Preview the native PDF, and "Post & send" to
 * publish it to the Client Hub for e-signature (asr_review). Usable from the opportunity (and the
 * project view later).
 */
import { useEffect, useRef, useState } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import { ctx } from '../../outline/ctx.js'
import { confirmDialog, alertDialog } from '../Dialog.jsx'
import { getOrCreateLink, upsertEntry, setStatus } from '../../services/journey.js'
import {
  createAsr, updateAsr, getAsrLineItems, saveAsrLineItems, previewAsrPdf, freezeAsrPdf, markAsrPosted,
  resetAsrSignature, ASR_FEE_TYPES, LINE_FEE_TYPES,
} from '../../services/asrs.js'

let _rk = 0
const newRow = () => ({ key: 'r' + (++_rk), item: '', feeType: 'Fixed', hours: 1, rate: '' })

export default function CrmAsrEditor({ asr, opportunityId, opportunityName, onClose, onSaved }) {
  const creating = !asr
  const [title, setTitle] = useState(asr ? asr.title : '')
  const [feeType, setFeeType] = useState(asr ? asr.feeType : 'Fixed')
  const [issueDate, setIssueDate] = useState(asr ? (asr.issueDate || '') : new Date().toISOString().slice(0, 10))
  const [stip, setStip] = useState(asr ? asr.stipulations : '')
  const [rows, setRows] = useState([newRow()])
  const [saved, setSaved] = useState(asr || null)
  const [busy, setBusy] = useState(false)
  const priorIds = useRef([])
  const loadedRef = useRef(false)

  useEffect(() => {
    if (loadedRef.current || !asr) return
    loadedRef.current = true
    getAsrLineItems(asr.lineItemIds).then(items => {
      priorIds.current = items.map(i => i.id)
      setRows(items.length ? items.map(i => ({ key: i.id, id: i.id, item: i.item, feeType: i.feeType, hours: i.hours || 1, rate: i.rate })) : [newRow()])
    }).catch(() => {})
  }, [asr])

  const setRow = (key, patch) => setRows(rs => rs.map(r => r.key === key ? { ...r, ...patch } : r))
  const total = rows.reduce((s, r) => s + (r.feeType === 'Hourly' ? 0 : (Number(r.hours || 1) * Number(r.rate || 0))), 0)

  async function save() {
    if (busy) return
    setBusy(true)
    try {
      let a = saved
      if (!a) a = await createAsr({ opportunityId, title, feeType, issueDate, stipulations: stip })
      else await updateAsr(a.id, { title, feeType, issueDate, stipulations: stip })
      if (a) {
        await saveAsrLineItems(a.id, rows.map(r => ({ id: r.id, item: r.item, feeType: r.feeType, hours: Number(r.hours || (r.feeType === 'Fixed' ? 1 : 0)), rate: Number(r.rate || 0) })), priorIds.current)
        setSaved(a)
        if (onSaved) onSaved()
      }
    } catch (e) { await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save the ASR.' }) }
    finally { setBusy(false) }
  }

  async function preview() {
    if (!saved) { await alertDialog({ title: 'Save first', message: 'Save the ASR before previewing.' }); return }
    const tab = window.open('', '_blank')
    try { const url = await previewAsrPdf(saved.id); if (tab) tab.location = url } catch (e) { if (tab) tab.close(); await alertDialog({ title: 'Preview failed', message: e.message }) }
  }

  async function post() {
    if (!saved) { await alertDialog({ title: 'Save first', message: 'Save the ASR before posting.' }); return }
    if (!(await confirmDialog({ title: 'Post & send to client?', message: 'Freezes the ASR document and publishes it to the Client Hub for review and e-signature.', confirmLabel: 'Post & send' }))) return
    setBusy(true)
    try {
      const frozen = await freezeAsrPdf(saved.id)
      await markAsrPosted(saved.id, { memberId: ctx.currentMember && ctx.currentMember.id })
      const { journey } = await getOrCreateLink(opportunityId, opportunityName)
      await upsertEntry({ journeyId: journey.id, type: 'asr_review', title: title || `ASR No. ${saved.number || ''}`, published: true,
        data: { asrId: saved.id, number: saved.number, title, totalFee: total, feeType, issueDate, stipulations: stip, pdfUrl: frozen.url } })
      try { await setStatus(journey.id, 'active') } catch (_) {}
      await alertDialog({ title: 'Posted', message: 'The ASR is live in the Client Hub for e-signature.' })
      if (onSaved) onSaved()
      onClose()
    } catch (e) { await alertDialog({ title: 'Post failed', message: e.message || 'Could not post.' }) }
    finally { setBusy(false) }
  }

  // Debug-gated test affordance: send a signed/posted ASR back to the journey unsigned to re-test.
  const debug = typeof document !== 'undefined' && document.body.classList.contains('aj-debug')
  async function resetSig() {
    if (busy || !saved) return
    if (!(await confirmDialog({ title: 'Reset signature (test)?', message: 'Sends this ASR back to the Client Hub as unsigned and clears its captured signature, for re-testing the e-sign flow.', confirmLabel: 'Reset', danger: true }))) return
    setBusy(true)
    try { await resetAsrSignature(saved.id); if (onSaved) onSaved(); await alertDialog({ title: 'Reset', message: 'The ASR is unsigned again and live in the Client Hub.' }) }
    catch (e) { await alertDialog({ title: 'Reset failed', message: e.message }) }
    finally { setBusy(false) }
  }

  const footer = (
    <>
      <button style={F.btnGhost} onClick={onClose} disabled={busy}>Close</button>
      {saved && <button style={F.btnGhost} onClick={preview} disabled={busy}>Preview PDF</button>}
      {saved && saved.posted && debug && <button style={{ ...F.btnGhost, color: '#b8540a' }} onClick={resetSig} disabled={busy} title="Test only — visible in /debug mode">↩ Reset (test)</button>}
      <button style={F.btnPrimary} onClick={save} disabled={busy}>{busy ? 'Saving…' : (creating && !saved ? 'Create' : 'Save')}</button>
      {saved && !saved.posted && <button style={F.btnPrimary} onClick={post} disabled={busy}>Post &amp; send</button>}
    </>
  )

  return (
    <CrmModal title={creating ? `New ASR — ${opportunityName || ''}` : (saved?.name || 'ASR')} onClose={onClose} footer={footer} width={680}>
      <div style={F.grid2}>
        <div style={F.row}><label style={F.label}>ASR title</label><input style={F.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Closet revision" /></div>
        <div style={F.row}><label style={F.label}>Issue date</label><input style={F.input} type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} /></div>
      </div>
      <div style={F.row}><label style={F.label}>Fee type</label><select style={F.input} value={feeType} onChange={e => setFeeType(e.target.value)}>{ASR_FEE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>

      <div style={S.liHead}><span style={S.liTitle}>Line items</span><div style={{ flex: 1 }} /><button style={{ ...F.btnGhost, padding: '4px 10px' }} onClick={() => setRows(rs => [...rs, newRow()])}>+ Add line</button></div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>Description</th><th style={S.thN}>Type</th><th style={S.thN}>Hours</th><th style={S.thN}>Rate / Amount</th><th style={S.thN}>Fee</th><th /></tr></thead>
        <tbody>
          {rows.map(r => {
            const fee = r.feeType === 'Hourly' ? null : Number(r.hours || 1) * Number(r.rate || 0)
            return (
              <tr key={r.key}>
                <td style={S.td}><input style={S.cell} value={r.item} onChange={e => setRow(r.key, { item: e.target.value })} /></td>
                <td style={S.tdN}><select style={S.cellS} value={r.feeType} onChange={e => setRow(r.key, { feeType: e.target.value })}>{LINE_FEE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></td>
                <td style={S.tdN}><input style={S.cellNum} type="number" min="0" step="0.5" value={r.hours} onChange={e => setRow(r.key, { hours: e.target.value })} /></td>
                <td style={S.tdN}><input style={S.cellNum} type="number" value={r.rate} onChange={e => setRow(r.key, { rate: e.target.value })} placeholder={r.feeType === 'Fixed' ? 'rate / amt' : '$/hr'} /></td>
                <td style={S.tdN}>{r.feeType === 'Hourly' ? <span style={S.dash}>hourly</span> : '$' + Math.round(fee || 0).toLocaleString()}</td>
                <td style={S.tdN}><button style={S.del} onClick={() => setRows(rs => rs.filter(x => x.key !== r.key))}>✕</button></td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div style={S.total}>Fixed total: <strong>${Math.round(total).toLocaleString()}</strong></div>

      <div style={{ ...F.row, marginTop: 12 }}><label style={F.label}>Stipulations / scope</label><textarea style={F.textarea} value={stip} onChange={e => setStip(e.target.value)} placeholder="What the additional services cover and any conditions." /></div>

      {saved && (saved.postedDoc || saved.signedDoc) && (
        <div style={S.docs}>
          {saved.postedDoc && <a href={saved.postedDoc} target="_blank" rel="noopener" style={S.docLink}>Posted ASR PDF ↗</a>}
          {saved.signedDoc && <a href={saved.signedDoc} target="_blank" rel="noopener" style={S.docLink}>Signed ASR (executed) ↗</a>}
        </div>
      )}
    </CrmModal>
  )
}

const S = {
  liHead: { display: 'flex', alignItems: 'center', marginTop: 14, marginBottom: 6 },
  liTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontFamily: '"DM Sans", sans-serif', fontSize: 10, fontWeight: 600, color: 'var(--aj-text-mid)', padding: '3px 5px', borderBottom: '0.5px solid var(--aj-border)' },
  thN: { textAlign: 'right', fontFamily: '"DM Sans", sans-serif', fontSize: 10, fontWeight: 600, color: 'var(--aj-text-mid)', padding: '3px 5px', borderBottom: '0.5px solid var(--aj-border)' },
  td: { padding: '4px 5px', borderBottom: '0.5px solid var(--aj-border)' },
  tdN: { padding: '4px 5px', textAlign: 'right', borderBottom: '0.5px solid var(--aj-border)' },
  cell: { width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', padding: '5px 6px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)', boxSizing: 'border-box' },
  cellS: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text)', padding: '5px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)' },
  cellNum: { width: 80, fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text)', padding: '5px 6px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)', textAlign: 'right' },
  dash: { color: 'var(--aj-text-mid)', fontSize: 12 },
  del: { background: 'none', border: 'none', color: 'var(--aj-text-mid)', cursor: 'pointer', fontSize: 12 },
  total: { textAlign: 'right', fontFamily: '"DM Mono", monospace', fontSize: 13, color: 'var(--aj-text)', marginTop: 8 },
  docs: { display: 'flex', gap: 16, marginTop: 14 },
  docLink: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#1a6fa8', textDecoration: 'none' },
}
